import React from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { buildTheme, ColorMode } from './index';
import { useBranding } from '../hooks/useBranding';
import type { TenantBranding } from '../types/branding';

type Props = { children: React.ReactNode };

export default function ThemeProvider({ children }: Props) {
  const [mode, setModeState] = React.useState<ColorMode>(() => (localStorage.getItem('ui_mode') as ColorMode) || 'light');
  const { branding, refresh: refreshBranding } = useBranding();

  const theme = React.useMemo(() => buildTheme(mode, branding), [mode, branding]);

  const setMode = React.useCallback((next: ColorMode) => {
    localStorage.setItem('ui_mode', next);
    setModeState(next);
  }, []);

  const toggleMode = React.useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  const contextValue = React.useMemo(
    () => ({ mode, setMode, toggleMode, branding, refreshBranding }),
    [mode, setMode, toggleMode, branding, refreshBranding],
  );

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <ModeContext.Provider value={contextValue}>
        {children}
      </ModeContext.Provider>
    </MuiThemeProvider>
  );
}

/**
 * Draws what is inside in another workspace's brand: its colours, charts and
 * logo. For a page shown to someone who is not signed in to that workspace (a
 * shared link), where the app's own branding, which comes with a login, is
 * not loaded. Keeps the light or dark mode already chosen.
 */
export function BrandScope({ branding, children }: { branding: TenantBranding | null; children: React.ReactNode }) {
  const outer = React.useContext(ModeContext);
  const theme = React.useMemo(() => buildTheme(outer.mode, branding), [outer.mode, branding]);
  const value = React.useMemo(() => ({ ...outer, branding }), [outer, branding]);
  if (!branding) return <>{children}</>;
  return (
    <MuiThemeProvider theme={theme}>
      <ModeContext.Provider value={value}>{children}</ModeContext.Provider>
    </MuiThemeProvider>
  );
}

interface ModeContextValue {
  mode: ColorMode;
  setMode: (next: ColorMode) => void;
  toggleMode: () => void;
  branding: TenantBranding | null;
  refreshBranding: () => void;
}

export const ModeContext = React.createContext<ModeContextValue>({
  mode: 'light',
  setMode: () => {},
  toggleMode: () => {},
  branding: null,
  refreshBranding: () => {},
});
